import { useState, useEffect, useRef } from "react";

const PROJECTS = [
  {
    id: "fit2",
    number: "01",
    title: "FIT2",
    subtitle: "Filter Integrity Tester",
    description:
      "End-to-end product development of a second-generation filter integrity testing instrument. Custom CAD, circuit boards, fluidic paths, and a fully integrated Linux-based hardware/software framework.",
    tags: ["SolidWorks", "Altium", "Flutter", "Linux"],
    status: "Production",
  },
  {
    id: "microscope",
    number: "02",
    title: "Cancer Cell Scanner",
    subtitle: "Capstone Project",
    description:
      "Automated cancer cell scanning microscope prototype. Precision motion control combined with computer vision for cell analysis.",
    tags: ["OpenCV", "Python", "3D Printing", "CNC"],
    status: "Prototype",
  },
  {
    id: "ble-mesh",
    number: "03",
    title: "BLE Mesh IMU",
    subtitle: "Sensor Network",
    description:
      "Custom driver library for the BNO086 9-axis IMU on the NRF52840, enabling Bluetooth mesh sensor networks for distributed motion tracking.",
    tags: ["NRF52840", "C", "BLE Mesh", "KiCad"],
    status: "Complete",
  },
  {
    id: "vr-treadmill",
    number: "04",
    title: "DIY VR Treadmill",
    subtitle: "Personal Project",
    description:
      "A custom-built omnidirectional VR treadmill combining mechanical design, embedded control, and software integration for immersive locomotion.",
    tags: ["Embedded", "CAD", "3D Printing", "C++"],
    status: "In Progress",
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Navbar({ scrolled }) {
  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        padding: "0 clamp(24px, 6vw, 96px)",
        height: 72,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: scrolled ? "rgba(252, 251, 249, 0.92)" : "transparent",
        backdropFilter: scrolled ? "blur(24px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(0,0,0,0.06)" : "1px solid transparent",
        transition: "all 0.5s ease",
      }}
    >
      <span
        style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 13,
          fontWeight: 500,
          color: "#1A1A1A",
          letterSpacing: "0.08em",
        }}
      >
        Edmund Loong
      </span>
      <div style={{ display: "flex", gap: 36 }}>
        {["Projects", "About", "Contact"].map((item) => (
          <a
            key={item}
            href={`#${item.toLowerCase()}`}
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              color: "#999",
              textDecoration: "none",
              letterSpacing: "0.06em",
              transition: "color 0.3s",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => (e.target.style.color = "#1A1A1A")}
            onMouseLeave={(e) => (e.target.style.color = "#999")}
          >
            {item}
          </a>
        ))}
      </div>
    </nav>
  );
}

function HeroSection({ visible }) {
  return (
    <section
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        padding: "0 clamp(24px, 6vw, 96px) clamp(60px, 10vh, 120px)",
      }}
    >
      <div
        style={{
          maxWidth: 600,
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(16px)",
          transition: "all 0.9s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <h1
          style={{
            fontFamily: "'IBM Plex Sans', 'Helvetica Neue', sans-serif",
            fontSize: "clamp(32px, 4.5vw, 52px)",
            fontWeight: 300,
            color: "#1A1A1A",
            lineHeight: 1.25,
            margin: 0,
            letterSpacing: "-0.02em",
          }}
        >
          Mechatronics engineer building instruments and embedded systems from concept to production.
        </h1>
      </div>

      <div
        style={{
          marginTop: 48,
          display: "flex",
          gap: 40,
          flexWrap: "wrap",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(12px)",
          transition: "all 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.15s",
        }}
      >
        {[
          { label: "Focus", value: "R&D / Instrumentation" },
          { label: "Location", value: "Melbourne, AU" },
          { label: "Experience", value: "3+ years" },
        ].map((item) => (
          <div key={item.label}>
            <div
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                color: "#BBB",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              {item.label}
            </div>
            <div
              style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 14,
                color: "#555",
                fontWeight: 400,
              }}
            >
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProjectCard({ project, visible, index }) {
  const [hovered, setHovered] = useState(false);

  const statusColor =
    project.status === "Production"
      ? "#2A9D5C"
      : project.status === "In Progress"
        ? "#D4880A"
        : "#999";

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "#FFFFFF" : "#F7F6F4",
        borderRadius: 8,
        padding: "clamp(28px, 3.5vw, 44px)",
        cursor: "pointer",
        transition: "all 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
        transitionDelay: `${index * 0.08}s`,
        opacity: visible ? 1 : 0,
        transform: visible
          ? hovered
            ? "translateY(-2px)"
            : "translateY(0)"
          : "translateY(24px)",
        boxShadow: hovered
          ? "0 8px 32px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)"
          : "0 0 0 rgba(0,0,0,0), 0 1px 2px rgba(0,0,0,0.02)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 28,
        }}
      >
        <span
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            color: "#CCC",
            fontWeight: 400,
          }}
        >
          {project.number}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: statusColor,
              opacity: 0.8,
            }}
          />
          <span
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: "#AAA",
              letterSpacing: "0.06em",
            }}
          >
            {project.status}
          </span>
        </div>
      </div>

      <h3
        style={{
          fontFamily: "'IBM Plex Sans', sans-serif",
          fontSize: "clamp(22px, 2.2vw, 28px)",
          fontWeight: 400,
          color: "#1A1A1A",
          margin: "0 0 4px 0",
          letterSpacing: "-0.01em",
        }}
      >
        {project.title}
      </h3>
      <div
        style={{
          fontFamily: "'IBM Plex Sans', sans-serif",
          fontSize: 13,
          color: "#AAA",
          marginBottom: 18,
        }}
      >
        {project.subtitle}
      </div>

      <p
        style={{
          fontFamily: "'IBM Plex Sans', sans-serif",
          fontSize: 14,
          color: "#888",
          lineHeight: 1.7,
          margin: "0 0 28px 0",
        }}
      >
        {project.description}
      </p>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {project.tags.map((tag) => (
          <span
            key={tag}
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: "#AAA",
              background: "rgba(0,0,0,0.03)",
              padding: "4px 10px",
              borderRadius: 4,
            }}
          >
            {tag}
          </span>
        ))}
      </div>

      <div
        style={{
          marginTop: 28,
          display: "flex",
          alignItems: "center",
          gap: 8,
          opacity: hovered ? 1 : 0,
          transform: hovered ? "translateX(0)" : "translateX(-6px)",
          transition: "all 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <span
          style={{
            fontFamily: "'IBM Plex Sans', sans-serif",
            fontSize: 13,
            color: "#1A1A1A",
            fontWeight: 500,
          }}
        >
          View project
        </span>
        <span style={{ fontSize: 14, color: "#1A1A1A" }}>→</span>
      </div>
    </div>
  );
}

function ProjectsSection({ visible }) {
  return (
    <section
      id="projects"
      style={{
        padding: "clamp(60px, 10vh, 120px) clamp(24px, 6vw, 96px)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 48,
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(16px)",
          transition: "all 0.7s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <h2
          style={{
            fontFamily: "'IBM Plex Sans', sans-serif",
            fontSize: 14,
            fontWeight: 500,
            color: "#1A1A1A",
            margin: 0,
            letterSpacing: "0.04em",
          }}
        >
          Selected Work
        </h2>
        <div
          style={{
            height: 1,
            flex: 1,
            background: "rgba(0,0,0,0.08)",
            margin: "0 20px",
          }}
        />
        <span
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            color: "#CCC",
          }}
        >
          {PROJECTS.length} projects
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 400px), 1fr))",
          gap: 16,
        }}
      >
        {PROJECTS.map((project, i) => (
          <ProjectCard
            key={project.id}
            project={project}
            index={i}
            visible={visible}
          />
        ))}
      </div>
    </section>
  );
}

function AboutSection({ visible }) {
  return (
    <section
      id="about"
      style={{
        padding: "clamp(40px, 8vh, 80px) clamp(24px, 6vw, 96px) clamp(80px, 14vh, 160px)",
      }}
    >
      <div
        style={{
          maxWidth: 540,
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(16px)",
          transition: "all 0.8s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <div
          style={{
            width: 32,
            height: 1,
            background: "#DDD",
            marginBottom: 32,
          }}
        />
        <p
          style={{
            fontFamily: "'IBM Plex Sans', sans-serif",
            fontSize: "clamp(18px, 2.2vw, 24px)",
            fontWeight: 300,
            color: "#555",
            lineHeight: 1.65,
            margin: "0 0 36px 0",
          }}
        >
          Specialising in end-to-end development of laboratory and industrial
          instrumentation — from CAD and circuit boards to firmware and user
          interfaces.
        </p>
        <a
          href="#about"
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            color: "#1A1A1A",
            textDecoration: "none",
            letterSpacing: "0.04em",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            paddingBottom: 2,
            borderBottom: "1px solid #DDD",
            transition: "border-color 0.3s, gap 0.3s",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#1A1A1A";
            e.currentTarget.style.gap = "12px";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#DDD";
            e.currentTarget.style.gap = "8px";
          }}
        >
          More about me <span style={{ fontSize: 13 }}>→</span>
        </a>
      </div>
    </section>
  );
}

function Footer() {
  const links = [
    { label: "GitHub", href: "#" },
    { label: "LinkedIn", href: "#" },
    { label: "Email", href: "#" },
  ];

  return (
    <footer
      id="contact"
      style={{
        padding: "32px clamp(24px, 6vw, 96px)",
        borderTop: "1px solid rgba(0,0,0,0.06)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 16,
      }}
    >
      <span
        style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          color: "#CCC",
          letterSpacing: "0.04em",
        }}
      >
        © 2026 Edmund Loong
      </span>
      <div style={{ display: "flex", gap: 28 }}>
        {links.map((link) => (
          <a
            key={link.label}
            href={link.href}
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: "#BBB",
              textDecoration: "none",
              letterSpacing: "0.04em",
              transition: "color 0.3s",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => (e.target.style.color = "#1A1A1A")}
            onMouseLeave={(e) => (e.target.style.color = "#BBB")}
          >
            {link.label}
          </a>
        ))}
      </div>
    </footer>
  );
}

export default function Portfolio() {
  const [scrolled, setScrolled] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const [projectsRef, projectsVisible] = useInView(0.08);
  const [aboutRef, aboutVisible] = useInView(0.2);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@300;400;500&display=swap');

        *, *::before, *::after {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        html {
          scroll-behavior: smooth;
        }

        body {
          background: #FCFBF9;
          color: #1A1A1A;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          overflow-x: hidden;
        }

        ::selection {
          background: rgba(26, 26, 26, 0.08);
        }
      `}</style>

      <div style={{ minHeight: "100vh" }}>
        <Navbar scrolled={scrolled} />
        <HeroSection visible={heroVisible} />

        <div ref={projectsRef}>
          <ProjectsSection visible={projectsVisible} />
        </div>

        <div ref={aboutRef}>
          <AboutSection visible={aboutVisible} />
        </div>

        <Footer />
      </div>
    </>
  );
}
